import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  CHALLENGE_BASIS_OPTIONS,
  submitEdLocationClaimChallenge,
} from "@healthdesk/shared-services";
import { isEmergencyDepartmentLocation } from "../../constants/diagnosticCapabilities";

/**
 * Tier 2 — challenge ownership of a claimed ED (verified physician, server-side NPI gate).
 */
export default function EdClaimChallengePanel({ location, locationId, onSubmitted }) {
  const [npi, setNpi] = useState("");
  const [basis, setBasis] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submitChallenge = async () => {
    setError("");
    const npiDigits = npi.replace(/\D/g, "");
    if (npiDigits.length !== 10) {
      setError("A valid 10-digit physician NPI is required.");
      return;
    }
    if (!basis) {
      setError("Please select a basis for this challenge.");
      return;
    }

    setLoading(true);
    try {
      await submitEdLocationClaimChallenge({
        locationId,
        npi: npiDigits,
        basis,
        note: note.trim() || undefined,
      });
      setSuccess(true);
      onSubmitted?.();
    } catch (err) {
      console.warn("[EdClaimChallengePanel] submit failed:", err);
      setError(err?.message || "Unable to submit challenge. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        Your challenge has been submitted for admin review. The current claim was not changed.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: "warning.main", borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        Challenge this claim
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        For verified physicians only. Contests who controls this listing. Requires your NPI;
        an administrator will review before any change.
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Physician NPI (10 digits)"
        value={npi}
        onChange={(e) => setNpi(e.target.value)}
        inputProps={{ inputMode: "numeric", maxLength: 10 }}
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="challenge-basis-label">Basis for challenge</InputLabel>
        <Select
          labelId="challenge-basis-label"
          value={basis}
          label="Basis for challenge"
          onChange={(e) => setBasis(e.target.value)}
        >
          {CHALLENGE_BASIS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="Additional details (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        sx={{ mb: 2 }}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      <Button variant="outlined" color="warning" size="small" onClick={submitChallenge} disabled={loading}>
        {loading ? "Submitting…" : "Submit challenge"}
      </Button>
    </Box>
  );
}

/**
 * @param {object | null | undefined} location
 * @param {boolean} isLoggedIn
 */
export function canShowEdClaimChallengePanel(location, isLoggedIn) {
  if (!isLoggedIn || !location || !isEmergencyDepartmentLocation(location)) return false;
  const users = location.users;
  return Array.isArray(users) && users.length > 0;
}
